const { getElement, getElements, formatMoney } = window.BAO.Utils

/**
 * @typedef {Object} SelectedVariantOption
 *
 * @property {string} label
 * @property {string} value
 */

const selectors = {
  addToCart: '[data-product-add-to-cart]',
  addToCartText: '[data-product-add-to-cart-text]',
  buyButtons: '[data-product-buy-buttons]',
  quantity: '[data-product-quantity]',
  form: '[data-product-form]',
  price: '[data-product-price]',
  priceCompare: '[data-product-price-compare]',
  priceCompareText: '[data-product-price-compare-text]',
  sku: '[data-product-sku]',
}

class Variant {
  constructor (data) {
    /**
     * @type {ShopifyVariant}
     * @public
     */
    this.data = data
  }

  setPrice () {
    unhidePriceEls()

    const priceElements = getElements(selectors.price)
    priceElements.forEach(el => (el.innerHTML = formatMoney(this.data.price)))

    const priceCompareElements = getElements(selectors.priceCompare)
    priceCompareElements.forEach(
      el => (el.innerHTML = formatMoney(this.data.compare_at_price)),
    )

    if (!this.data.compare_at_price) {
      hidePriceCompareElements()
    }

    getElements(selectors.price).forEach(priceEl => {
      priceEl.classList.toggle(
        priceEl.dataset.saleClass,
        !!this.data.compare_at_price,
      )
    })

    const yotpoPointElement = getElement('[loyalty-points]')

    if (!yotpoPointElement) return

    yotpoPointElement.dataset.variantId = this.id
  }

  setAvailability (event) {
    const quickBuyEl = event.target.closest(`product-quickbuy`)
    if (quickBuyEl) {
      const addToCartEls = getElements(selectors.addToCart, {
        context: quickBuyEl,
      })
      const quickbuyNotifyMeEls = getElements(
        `[data-product-quickbuy-back-in-stock]`,
        { context: quickBuyEl },
      )
      this.handleNotify(addToCartEls, quickbuyNotifyMeEls, this.data.available)
    } else {
      const addToCartEls = getElements(selectors.addToCart)
      const notifyMeEls = getElements(`[data-product-back-in-stock]`)
      this.handleNotify(addToCartEls, notifyMeEls, this.data.available)
    }
  }

  handleNotify (addToCartEls, notifyEls, available) {
    if (!available) {
      addToCartEls.forEach(addToCartEl =>
        addToCartEl.setAttribute(`aria-hidden`, true),
      )
      notifyEls.forEach(notifyEl => {
        notifyEl.setAttribute(`aria-hidden`, false)
        notifyEl.setAttribute(`data-variant-id`, this.data.id)
        notifyEl.setAttribute(`data-variant-title`, this.data.name)
      })
    } else {
      addToCartEls.forEach(addToCartEl =>
        addToCartEl.setAttribute(`aria-hidden`, false),
      )
      notifyEls.forEach(notifyEl => {
        notifyEl.setAttribute(`aria-hidden`, true)
      })
    }
  }

  toggleQuantityInput (availability) {
    const quantityInputs = getElements(selectors.quantity)
    quantityInputs.forEach(el => el.setAttribute(`aria-hidden`, !availability))
  }

  updateInput (event) {
    const form = event.target.closest(`form`)
    const internalInputs = form
      ? getElements(`[name="id"]`, { context: form })
      : []
    const externalInputs = getElements(`[form="${this.formId}"][name="id"]`)
    const inputs = [...internalInputs, ...externalInputs]
    inputs.forEach(input => {
      input.value = this.id
      input.dispatchEvent(new Event('change', { bubbles: true }))
    })
  }

  buttonCheck (variant, options) {
    options.forEach(option => {
      if (option.id !== variant.id) return
      const hideButton = option.hide_button
      const buttonText = option.button_text
      const addToCartEls = getElements(selectors.addToCart)
      const notifyMeEls = getElements(`[data-product-back-in-stock]`)

      if (!variant.data.available) {
        notifyMeEls.forEach(notifyEl => {
          notifyEl.disabled = hideButton
          notifyEl.innerText = buttonText
          notifyEl.setAttribute(`aria-hidden`, false)
        })
        addToCartEls.forEach(addToCartEl =>
          addToCartEl.setAttribute(`aria-hidden`, true),
        )
      } else {
        addToCartEls.forEach(addToCartEl =>
          addToCartEl.setAttribute(`aria-hidden`, false),
        )
        notifyMeEls.forEach(notifyEl => {
          notifyEl.setAttribute(`aria-hidden`, true)
        })
      }
    })
  }

  setUrl () {
    window.history.replaceState(
      { path: this.url.toString() },
      '',
      this.url.toString(),
    )
  }

  setShareUrl () {
    const shareButtons = getElements('share-button')
    if (shareButtons.length === 0) return

    shareButtons.forEach(shareButton => {
      shareButton.updateUrl(this.url.toString())
    })
  }

  /**
   * @returns {number}
   */
  get id () {
    return this.data.id
  }

  /**
   * @returns {URL}
   */
  get url () {
    // eslint-disable-next-line compat/compat
    const params = new URLSearchParams(window.location.search)
    params.set('variant', this.id.toString())

    // eslint-disable-next-line compat/compat
    const url = new URL(window.location.href)
    url.search = params.toString()

    return url
  }

  get notifyMeEls () {
    return getElements(`[data-product-back-in-stock]`)
  }

  get quickBuyFormSelector () {
    return `[data-product-quickbuy-form]`
  }

  set formId (formId) {
    this._formId = formId
  }

  get formId () {
    return this._formId
  }
}

class VariantOptions extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['data', 'option', 'option-choice', 'option-current']
  }

  constructor () {
    super()

    /**
     * @type {Variant[]}
     * @public
     */
    this.collectedVariants = [new Variant(this.defaultVariant)]
    this.setOptionAvailability()
  }

  setOptionAvailability () {
    const allVariants = this.data

    for (let i = 1; i < 3; i++) {
      const optionIndex = `option${i}`
      const availableOptionValues = [
        ...new Set(
          allVariants
            .filter(variant => variant.available)
            .map(variant => variant[optionIndex]),
        ),
      ]
      this.optionChoiceEls
        .filter(
          optionEl =>
            optionEl.dataset.index.toString() === optionIndex.toString(),
        )
        .forEach(optionEl => {
          const isAvailable = availableOptionValues.find(
            value => value.toString() === optionEl.value.toString(),
          )
          const labelEl = getElement('label', {
            context: optionEl.parentNode,
          })
          if (!labelEl) return
          labelEl.setAttribute('data-available', (!!isAvailable).toString())
        })
    }
  }

  setupListeners () {
    super.setupListeners()

    this.onVariantChange = this.onVariantChange.bind(this)

    this.listeners.add(this, 'change', this.onVariantChange)
  }

  onVariantChange (event) {
    const variant = this.getVariant()
    this.dispatchVariantChangeEvent(event, variant)
    this.updateCurrentOptionEl(event)
    variant.formId = this?.dataset?.formId
    variant.setAvailability(event)
    variant.setPrice()
    variant.updateInput(event)
    variant.buttonCheck(variant, this.metafields)

    if (!this.currentVariant) {
      this.resetPage(variant)

      return
    }

    variant.setUrl()
    variant.setShareUrl()
  }

  dispatchVariantChangeEvent (event, variant) {
    this.dispatchEvent(
      new CustomEvent(window.BAO.EVENTS.VARIANT.CHANGE, {
        detail: {
          event,
          variant,
        },
        bubbles: true,
      }),
    )
  }

  updateCurrentOptionEl (event) {
    const radioEl = event.target
    const fieldsetEl = radioEl.closest(this.els?.option?.selector)
    const currentEl = fieldsetEl
      ? getElement(this.els['option-current']?.selector, {
        context: fieldsetEl,
      })
      : null
    if (!currentEl) return
    currentEl.textContent = event.target.value
  }

  /**
   * @param {Variant} variant
   *
   * @returns {void}
   */
  resetPage (variant) {
    window.history.replaceState(
      { path: window.location.pathname },
      '',
      window.location.pathname,
    )
  }

  /**
   * @param {ShopifyVariant} data
   *
   * @returns {Variant}
   */
  createVariant (data) {
    const variant = new Variant(data)
    this.collectedVariants.push(variant)

    return variant
  }

  /**
   * @returns {Variant}
   */
  getVariant () {
    if (!this.currentVariant) {
      return this.collectedVariants[0]
    }

    const existingVariant = this.collectedVariants.find(
      variant => variant.id === this?.currentVariant?.id,
    )
    if (existingVariant) return existingVariant

    return this.createVariant(this.currentVariant)
  }

  /**
   * @returns {SelectedVariantOption[]}
   */
  get selectedOptions () {
    this.throwError(`selectedOptions needs to be implemented on this class`)

    return []
  }

  /**
   * @returns {ShopifyVariant}
   */
  get defaultVariant () {
    return this.data[0]
  }

  /**
   * @returns {ShopifyVariant|undefined}
   */
  get currentVariant () {
    return this.data.find(variant => {
      return variant.options.every(
        (option, index) => this.selectedOptions[index].value === option,
      )
    })
  }

  /**
   * @returns {ShopifyVariant[]}
   */
  get data () {
    return JSON.parse(this.els.data.element.innerText)
  }

  /**
   * @returns {ShopifyVariant[]}
   */
  get metafields () {
    return JSON.parse(this.els.metafields.element.innerText)
  }

  get optionChoiceEls () {
    return this.els['option-choice']?.elements || []
  }
}

class VariantRadios extends VariantOptions {
  get selectedOptions () {
    return this.els.option.elements.map(el => {
      const { name } = el.dataset
      const choice = getElements(this.els['option-choice'].selector, {
        context: el,
      }).find(el => el.checked).value

      return {
        label: name,
        value: choice,
      }
    })
  }
}

class VariantSelects extends VariantOptions {
  get selectedOptions () {
    return this.els.option.elements.map(el => {
      const { name } = el.dataset

      return {
        label: name,
        value: el.querySelector('select').value,
      }
    })
  }
}

if (!customElements.get('variant-radios')) {
  customElements.define('variant-radios', VariantRadios)
}

if (!customElements.get('variant-selects')) {
  customElements.define('variant-selects', VariantSelects)
}

function hideElements (elements) {
  elements.forEach(el => (el.hidden = true))
}

function unhideElements (elements) {
  elements.forEach(el => (el.hidden = false))
}

function unhidePriceEls () {
  unhideElements(getElements(`${selectors.price}, ${selectors.priceCompare}`))
}

function hidePriceCompareElements () {
  hideElements(getElements(selectors.priceCompare))
}
